import {
  AccessoryProductType,
  AirHandlerProductType,
  ConfigurationType,
  DuctingComponentProductType,
  SelectedSystemType
} from 'data/types';
import { plenumNeedForFit } from 'utils/concealed-plenum-dimensions';
import {
  NeededDuctComponentType,
  ZoneDuctingSidePlanType,
  annotateSuppliesWithStock,
  resolveDuctConsumables,
  resolveZoneDuctingPlan,
  summarizeSupplyLineStock
} from 'utils/duct-component-fallback';
import {
  ZoneDuctSuppliesType,
  buildZoneDuctSupplies
} from 'utils/duct-supplies';
import {
  DUCT_MODE_KEYS,
  RadialDuctModeKeyType,
  deriveNeededZoneDuctingProducts,
  findZoneDuctingEvaluation,
  getZoneDuctingModes,
  isConcealedDuctProduct,
  passingDuctModeKeys,
  resolveZoneDuctModeKey
} from 'utils/ducting-products';
import {
  ductSuppliesModeForKey,
  ZoneDuctModesMapType
} from 'utils/zone-duct-modes';

export type DuctingComponentRecommendationType = {
  systemUniqueId: string;
  productType: 'air_handler' | 'system';
  productId: number;
  uniqueId: string;
  accessoryProductId: number;
  accessoryType: 'air_distribution';
  recommendedPriority: 1;
  accessoryName: string;
  accessoryDescription: string;
  accessoryPrice: number;
  accessoryProductUrl: string;
  accessoryPhoto: string;
  accessoryAvailableStock: number;
  accessoryOriginalType: string;
  accessoryShortName: string;
  recommendedQuantity: number;
  lineSetSettings?: undefined;
};

type BuildParams = {
  systems: SelectedSystemType[];
  configurations: ConfigurationType[];
  ductingKitProducts: AccessoryProductType[];
  airHandlerProducts: AirHandlerProductType[];
  ductingComponents: DuctingComponentProductType[];
  zoneDuctModes?: ZoneDuctModesMapType;
};

export type DemotedKitType = {
  zoneUniqueId: string;
  kitProductId: number;
};

export type DuctingRecommendationsResultType = {
  recommendations: DuctingComponentRecommendationType[];
  demotedKits: DemotedKitType[];
};

export type ZoneDuctModeDetailType = {
  allPartsAvailable: boolean;
  stockedCount: number;
  totalCount: number;
  missing: string[];
  supplies: ZoneDuctSuppliesType;
};

export type ZoneDuctModeOptionType = {
  zoneUniqueId: string;
  selectedKey: RadialDuctModeKeyType;
  passingModeCount: number;
  details: Partial<Record<RadialDuctModeKeyType, ZoneDuctModeDetailType>>;
};

function sidePlanAvailable(side: ZoneDuctingSidePlanType): boolean {
  if (side.source === 'kit') return true;
  if (side.source === 'components') return side.componentsComplete;
  return side.runCount === 0;
}

export function buildZoneDuctModeOptions({
  systems,
  configurations,
  ductingKitProducts,
  airHandlerProducts,
  ductingComponents,
  zoneDuctModes
}: {
  systems: SelectedSystemType[];
  configurations: ConfigurationType[];
  ductingKitProducts: AccessoryProductType[];
  airHandlerProducts: AirHandlerProductType[];
  ductingComponents: DuctingComponentProductType[];
  zoneDuctModes?: ZoneDuctModesMapType;
}): ZoneDuctModeOptionType[] {
  const options: ZoneDuctModeOptionType[] = [];
  systems.forEach((system) => {
    const sourceConfig = configurations.find(
      (config) => config.uniqueId === system.configuration.uniqueId
    );
    system.configuration.condensers.forEach((condenser) => {
      const validSystems =
        sourceConfig?.condensers.find(
          (sourceCondenser) => sourceCondenser.uniqueId === condenser.uniqueId
        )?.validSystems ?? [];
      condenser.zones.forEach((zone) => {
        const zoneProduct = zone.selectedProduct;
        if (!zoneProduct || !isConcealedDuctProduct(zoneProduct)) return;
        const catalogProduct =
          airHandlerProducts.find(
            (product) => product.productId === zoneProduct.productId
          ) ?? zoneProduct;
        const ducting = findZoneDuctingEvaluation(
          validSystems,
          zone.uniqueId,
          zoneProduct.productId
        );
        const modes = getZoneDuctingModes(ducting);
        const selectedKey = resolveZoneDuctModeKey(
          modes,
          zoneDuctModes?.[zone.uniqueId]
        );
        if (!selectedKey) return;

        const roomNamesByUniqueId = new Map(
          zone.zoneItems.map((item) => [item.structureItemId, item.name])
        );
        const details: ZoneDuctModeOptionType['details'] = {};
        DUCT_MODE_KEYS.forEach((modeKey) => {
          const evaluation = modes[modeKey];
          if (!evaluation?.pass || !evaluation.metrics) return;
          const suppliesMode = ductSuppliesModeForKey(modeKey);
          const modeDucting = {
            airHandlerProductId: zoneProduct.productId,
            metrics: evaluation.metrics,
            reasons: evaluation.reasons
          };
          const modeNeeded = deriveNeededZoneDuctingProducts({
            airHandlerProduct: catalogProduct,
            ducting: modeDucting as typeof ducting,
            ductingKitProducts,
            mode: suppliesMode
          });
          const plan = resolveZoneDuctingPlan(
            evaluation.metrics,
            ductingComponents,
            suppliesMode,
            modeNeeded
          );
          const supplies = buildZoneDuctSupplies(
            evaluation.metrics,
            roomNamesByUniqueId,
            {
              supplyCoveredByLabel: plan.supply.source === 'kit' ? 'kit' : null,
              returnCoveredByLabel: plan.return.source === 'kit' ? 'kit' : null
            },
            suppliesMode,
            plenumNeedForFit(
              modeNeeded?.plenumFit,
              modeNeeded?.requiredPlenumDims
            )
          );
          const annotated = annotateSuppliesWithStock(
            supplies,
            ductingComponents
          );
          const stockSummary = summarizeSupplyLineStock(annotated.supplies);
          details[modeKey] = {
            ...stockSummary,
            supplies: annotated.supplies,
            allPartsAvailable:
              sidePlanAvailable(plan.supply) &&
              sidePlanAvailable(plan.return) &&
              stockSummary.stockedCount === stockSummary.totalCount
          };
        });

        options.push({
          zoneUniqueId: zone.uniqueId,
          selectedKey,
          passingModeCount: passingDuctModeKeys(modes).length,
          details
        });
      });
    });
  });
  return options;
}

function toRecommendation(
  systemUniqueId: string,
  target: {
    productType: 'air_handler' | 'system';
    uniqueId: string;
    productId: number;
  },
  needed: NeededDuctComponentType
): DuctingComponentRecommendationType {
  return {
    systemUniqueId,
    productType: target.productType,
    productId: target.productId,
    uniqueId: target.uniqueId,
    accessoryProductId: needed.product.productId,
    accessoryType: 'air_distribution',
    recommendedPriority: 1,
    accessoryName: needed.product.name,
    accessoryDescription: needed.product.description,
    accessoryPrice: needed.product.price,
    accessoryProductUrl: needed.product.productUrl,
    accessoryPhoto: needed.product.photo,
    accessoryAvailableStock: needed.product.availableStock,
    accessoryOriginalType: 'ducting',
    accessoryShortName: needed.product.name,
    recommendedQuantity: needed.quantity
  };
}

export function buildDuctingComponentRecommendations({
  systems,
  configurations,
  ductingKitProducts,
  airHandlerProducts,
  ductingComponents,
  zoneDuctModes
}: BuildParams): DuctingRecommendationsResultType {
  const recommendations: DuctingComponentRecommendationType[] = [];
  const demotedKits: DemotedKitType[] = [];
  if (ductingComponents.length === 0) {
    return { recommendations, demotedKits };
  }

  systems.forEach((system) => {
    const sourceConfig = configurations.find(
      (config) => config.uniqueId === system.configuration.uniqueId
    );

    let systemSupplyRunCount = 0;
    let systemReturnOpeningCount = 0;
    let hasComponentRecommendations = false;

    system.configuration.condensers.forEach((condenser) => {
      const validSystems =
        sourceConfig?.condensers.find(
          (sourceCondenser) => sourceCondenser.uniqueId === condenser.uniqueId
        )?.validSystems ?? [];

      condenser.zones.forEach((zone) => {
        const zoneProduct = zone.selectedProduct;
        if (!zoneProduct || !isConcealedDuctProduct(zoneProduct)) return;

        const catalogProduct =
          airHandlerProducts.find(
            (product) => product.productId === zoneProduct.productId
          ) ?? zoneProduct;

        const ducting = findZoneDuctingEvaluation(
          validSystems,
          zone.uniqueId,
          zoneProduct.productId
        );
        const modes = getZoneDuctingModes(ducting);
        const preferredKey = resolveZoneDuctModeKey(
          modes,
          zoneDuctModes?.[zone.uniqueId]
        );
        const preferredEvaluation = preferredKey ? modes[preferredKey] : null;
        const metrics =
          preferredEvaluation?.metrics ?? ducting?.metrics ?? null;
        if (!metrics) return;

        const suppliesMode = preferredKey
          ? ductSuppliesModeForKey(preferredKey)
          : 'flex_only';
        const selectedDucting = preferredEvaluation
          ? {
              airHandlerProductId: zoneProduct.productId,
              metrics: preferredEvaluation.metrics,
              reasons: preferredEvaluation.reasons
            }
          : ducting;
        const needed = deriveNeededZoneDuctingProducts({
          airHandlerProduct: catalogProduct,
          ducting: selectedDucting as typeof ducting,
          ductingKitProducts,
          mode: suppliesMode
        });

        const plan = resolveZoneDuctingPlan(
          metrics,
          ductingComponents,
          suppliesMode,
          needed
        );

        const zoneComponents = new Map<number, NeededDuctComponentType>();
        [plan.supply, plan.return].forEach((side) => {
          if (side.source !== 'components') return;
          side.components.forEach((component) => {
            const existing = zoneComponents.get(component.product.productId);
            if (existing) {
              existing.quantity += component.quantity;
            } else {
              zoneComponents.set(component.product.productId, {
                ...component
              });
            }
          });
          if (side.kit) {
            demotedKits.push({
              zoneUniqueId: zone.uniqueId,
              kitProductId: side.kit.product.productId
            });
          }
        });

        if (plan.supply.source === 'components') {
          systemSupplyRunCount += plan.supply.runCount;
        }
        if (plan.return.source === 'components') {
          systemReturnOpeningCount += plan.return.runCount;
        }
        if (zoneComponents.size > 0) {
          hasComponentRecommendations = true;
        }

        zoneComponents.forEach((component) => {
          recommendations.push(
            toRecommendation(
              system.uniqueId,
              {
                productType: 'air_handler',
                uniqueId: zone.uniqueId,
                productId: zoneProduct.productId
              },
              component
            )
          );
        });
      });
    });

    if (hasComponentRecommendations) {
      resolveDuctConsumables(ductingComponents, {
        supplyRunCount: systemSupplyRunCount,
        returnOpeningCount: systemReturnOpeningCount
      }).forEach((consumable) => {
        recommendations.push(
          toRecommendation(
            system.uniqueId,
            {
              productType: 'system',
              uniqueId: system.uniqueId,
              productId: 0
            },
            consumable
          )
        );
      });
    }
  });

  return { recommendations, demotedKits };
}
